import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { 
  FileText, 
  Search, 
  MessageSquare, 
  CheckCircle, 
  ArrowRight, 
  Clock, 
  Shield, 
  TrendingUp,
  Users,
  Zap,
  Target
} from 'lucide-react'

export function HowItWorks() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use SourcedIn RFP Marketplace",
    "description": "Complete guide on how to use SourcedIn's RFP marketplace for both buyers and vendors",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Create Your Account",
        "text": "Sign up and set up your business profile with company details and requirements"
      },
      {
        "@type": "HowToStep", 
        "name": "Post or Browse RFPs",
        "text": "Buyers post RFPs with detailed requirements, vendors browse and find matching opportunities"
      },
      {
        "@type": "HowToStep",
        "name": "Smart Matching",
        "text": "Our AI algorithm matches RFPs with the most qualified vendors based on expertise and criteria"
      },
      {
        "@type": "HowToStep",
        "name": "Submit Proposals",
        "text": "Vendors submit competitive proposals, buyers receive and evaluate submissions"
      },
      {
        "@type": "HowToStep",
        "name": "Connect and Close",
        "text": "Direct communication between parties to finalize details and close successful deals"
      }
    ]
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="How SourcedIn RFP Marketplace Works | Complete Guide for Buyers & Vendors"
        description="Learn how SourcedIn's RFP marketplace connects buyers and vendors. Step-by-step guide to posting RFPs, finding opportunities, submitting proposals, and closing deals successfully."
        keywords="how RFP marketplace works, request for proposal process, vendor bidding, procurement process, B2B marketplace guide, RFP submission, vendor matching"
        canonical="https://sourcedin.ai/how-it-works"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            How SourcedIn 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Works</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Discover how our intelligent RFP marketplace streamlines procurement, 
            connects the right businesses, and accelerates successful partnerships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4">
                Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                Browse RFPs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Stats */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">5 Min</div>
              <div className="text-gray-600">Average Setup Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">70%</div>
              <div className="text-gray-600">Faster Procurement</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">95%</div>
              <div className="text-gray-600">Match Success Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-gray-600">Platform Availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* For Buyers Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              For Buyers: Post RFPs & Find Vendors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Streamline your procurement process and connect with qualified vendors in minutes
            </p>
          </div>

          <div className="space-y-12">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    1
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Create Your RFP</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Use our guided RFP creation wizard to define your project requirements, 
                  budget, timeline, and evaluation criteria. Our templates ensure you 
                  capture all essential details for maximum vendor response quality.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Detailed project specifications</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Budget range and payment terms</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Timeline and milestone requirements</span>
                  </div>
                </div>
              </div>
              <Card className="p-8 border-0 shadow-lg">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <FileText className="h-12 w-12 text-blue-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">Smart RFP Builder</h4>
                  <p className="text-gray-600">
                    Our AI-powered builder suggests optimal structure, 
                    required fields, and best practices based on your industry.
                  </p>
                </div>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card className="p-8 border-0 shadow-lg md:order-2">
                <div className="bg-purple-50 p-6 rounded-lg">
                  <Search className="h-12 w-12 text-purple-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">AI-Powered Matching</h4>
                  <p className="text-gray-600">
                    Our algorithm instantly identifies and notifies the most 
                    qualified vendors based on expertise, location, and track record.
                  </p>
                </div>
              </Card>
              <div className="md:order-1">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    2
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Get Matched Instantly</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Once published, our AI immediately analyzes your RFP and matches it 
                  with relevant vendors in our network. Qualified suppliers receive 
                  instant notifications and can respond within hours.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Instant vendor notifications</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Quality-scored vendor profiles</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Geographic and expertise filtering</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    3
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Evaluate & Select</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Review proposals using our built-in comparison tools, communicate 
                  directly with vendors through our secure messaging system, and make 
                  informed decisions based on comprehensive vendor profiles and ratings.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Side-by-side proposal comparison</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Secure vendor communication</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Verified vendor credentials</span>
                  </div>
                </div>
              </div>
              <Card className="p-8 border-0 shadow-lg">
                <div className="bg-green-50 p-6 rounded-lg">
                  <MessageSquare className="h-12 w-12 text-green-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">Smart Evaluation</h4>
                  <p className="text-gray-600">
                    Compare proposals with automated scoring, vendor ratings, 
                    and direct communication tools for informed decision-making.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* For Vendors Process */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              For Vendors: Find RFPs & Win Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover relevant opportunities and submit winning proposals that showcase your expertise
            </p>
          </div>

          <div className="space-y-12">
            {/* Vendor Step 1 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card className="p-8 border-0 shadow-lg">
                <div className="bg-orange-50 p-6 rounded-lg">
                  <Users className="h-12 w-12 text-orange-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">Complete Profile</h4>
                  <p className="text-gray-600">
                    Build a comprehensive vendor profile showcasing your capabilities, 
                    past projects, and client testimonials.
                  </p>
                </div>
              </Card>
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    1
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Set Up Your Profile</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Create a compelling vendor profile that highlights your expertise, 
                  showcases past successes, and builds trust with potential buyers. 
                  Our profile optimization tools help you stand out from the competition.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Portfolio and case studies</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Client testimonials and ratings</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Expertise and certification badges</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Step 2 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    2
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Discover Opportunities</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Receive personalized RFP recommendations based on your expertise and 
                  preferences. Our smart alerts ensure you never miss relevant opportunities 
                  that match your business capabilities.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Personalized RFP recommendations</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Real-time opportunity alerts</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Advanced filtering and search</span>
                  </div>
                </div>
              </div>
              <Card className="p-8 border-0 shadow-lg">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <Target className="h-12 w-12 text-blue-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">Smart Matching</h4>
                  <p className="text-gray-600">
                    Get matched with RFPs that perfectly align with your expertise, 
                    location, and business goals for higher win rates.
                  </p>
                </div>
              </Card>
            </div>

            {/* Vendor Step 3 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card className="p-8 border-0 shadow-lg">
                <div className="bg-purple-50 p-6 rounded-lg">
                  <Zap className="h-12 w-12 text-purple-600 mb-4" />
                  <h4 className="text-xl font-bold mb-2">Winning Proposals</h4>
                  <p className="text-gray-600">
                    Use our proposal templates and guidance to create compelling 
                    submissions that win more business.
                  </p>
                </div>
              </Card>
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    3
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Submit Proposals</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Create compelling proposals using our guided templates and best practice 
                  recommendations. Track your submission status and engage directly with 
                  buyers to clarify requirements and build relationships.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Professional proposal templates</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Real-time submission tracking</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700">Direct buyer communication</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why SourcedIn Works Better
            </h2>
            <p className="text-xl text-gray-600">
              Advanced features that make procurement more efficient and successful
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">70% Faster</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Automated matching and streamlined workflows reduce procurement time from months to days.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">100% Secure</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Enterprise-grade security with encrypted communications and verified vendor credentials.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">95% Success</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Industry-leading match success rate with intelligent vendor-buyer pairing algorithms.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of successful businesses already using SourcedIn to grow faster and smarter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
                Explore RFPs Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
